/*
 * <heritage-cart>
 *
 * Interactive shell around the cart page. The underlying <form> still
 * posts natively to /cart, so a JS-disabled visitor can still update
 * quantities (Update button) and proceed to checkout. With JS, this
 * component intercepts:
 *   - quantity +/- buttons
 *   - quantity input changes (blur or Enter)
 *   - Remove button clicks
 *   - debounced order-notes typing
 * and routes them through /cart/change.js + /cart/update.js so the
 * page reflects the new state without a full reload.
 *
 * Events dispatched on `document`:
 *   - `mdj:cart-updated` { cart } — fired after every successful change,
 *     consumed by the header cart-count refresh.
 *
 * Money formatting uses the shop's currency format if present
 * (window.Shopify.currency.active), falling back to USD.
 */

class HeritageCart extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onInput = this.onInput.bind(this);
    this.noteTimer = null;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-heritage-cart-form]');
    this.stockWarningTemplate =
      this.getAttribute('data-stock-warning-template') ||
      'Only {count} in stock.';
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    if (this.noteTimer) clearTimeout(this.noteTimer);
  }

  /* ------------------------------- events ------------------------------ */

  onClick(event) {
    const dec = event.target.closest('[data-heritage-cart-decrement]');
    const inc = event.target.closest('[data-heritage-cart-increment]');
    const rem = event.target.closest('[data-heritage-cart-remove]');
    if (!dec && !inc && !rem) return;
    event.preventDefault();

    const line = event.target.closest('[data-heritage-cart-line]');
    if (!line) return;

    if (rem) {
      this.updateLine(line, 0);
      return;
    }

    const input = line.querySelector('[data-heritage-cart-qty-input]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 0;
    let next = inc ? current + 1 : Math.max(0, current - 1);

    // Inventory cap — the Liquid sets data-max-quantity from
    // variant.inventory_quantity when tracking is on AND the variant's
    // inventory_policy denies overselling. Each MDJ piece is one-of-one
    // in practice, so the cap is almost always 1; if a partner clicks
    // "+" past that, paint the warning and clamp.
    const max = this.readMax(input);
    if (max != null && next > max) {
      this.showStockWarning(line, max);
      next = max;
      if (next === current) return;
    } else {
      this.clearStockWarning(line);
    }

    this.updateLine(line, next);
  }

  onChange(event) {
    const input = event.target.closest('[data-heritage-cart-qty-input]');
    if (!input) return;
    const line = input.closest('[data-heritage-cart-line]');
    if (!line) return;
    let next = Math.max(0, parseInt(input.value, 10) || 0);

    const max = this.readMax(input);
    if (max != null && next > max) {
      this.showStockWarning(line, max);
      next = max;
      input.value = String(next);
    } else {
      this.clearStockWarning(line);
    }

    this.updateLine(line, next);
  }

  readMax(input) {
    const raw = input.getAttribute('data-max-quantity');
    if (raw == null || raw === '') return null;
    const n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  showStockWarning(line, max) {
    const el = line.querySelector('[data-heritage-cart-qty-warning]');
    if (!el) return;
    el.textContent = this.stockWarningTemplate.replace('{count}', String(max));
    el.hidden = false;
  }

  clearStockWarning(line) {
    const el = line.querySelector('[data-heritage-cart-qty-warning]');
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  onInput(event) {
    const note = event.target.closest('#cart-note');
    if (!note) return;
    if (this.noteTimer) clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => this.updateNote(note.value), 600);
  }

  /* ------------------------------- requests ---------------------------- */

  async updateLine(lineEl, quantity) {
    const key = lineEl.dataset.lineKey;
    if (!key) return;

    this.setBusy(lineEl, true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!res.ok) throw new Error('change-failed');
      const cart = await res.json();

      if (quantity === 0) {
        this.removeLineEl(lineEl);
      } else {
        const item = (cart.items || []).find((i) => i.key === key);
        if (item) this.applyLineUpdate(lineEl, item);
      }

      this.applyTotals(cart);
      this.applyEmptyState(cart);
      document.dispatchEvent(new CustomEvent('mdj:cart-updated', { detail: { cart } }));
    } catch (err) {
      // On failure, fall back to native form submission (Update button).
      const submitter = this.form && this.form.querySelector('[name="update"]');
      if (submitter) submitter.click();
      else if (this.form) this.form.submit();
    } finally {
      this.setBusy(lineEl, false);
    }
  }

  async updateNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ note })
      });
    } catch (err) {
      /* silent — note persists locally and ships on checkout submit */
    }
  }

  /* ------------------------------- DOM updates ------------------------- */

  setBusy(lineEl, busy) {
    lineEl.dataset.busy = busy ? 'true' : 'false';
  }

  removeLineEl(lineEl) {
    lineEl.remove();
  }

  applyLineUpdate(lineEl, item) {
    const qtyInput = lineEl.querySelector('[data-heritage-cart-qty-input]');
    if (qtyInput) qtyInput.value = String(item.quantity);
    const totalEl = lineEl.querySelector('[data-heritage-cart-line-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(item.final_line_price);
  }

  applyTotals(cart) {
    const sub = this.querySelector('[data-heritage-cart-subtotal]');
    if (sub) sub.textContent = this.formatMoney(cart.total_price);

    const count = this.querySelector('[data-heritage-cart-summary-count]');
    if (count) {
      count.textContent = cart.item_count === 1 ? '1 piece' : cart.item_count + ' pieces';
    }
  }

  applyEmptyState(cart) {
    if (cart.item_count > 0) return;
    // Reload to let Liquid render the merchant-editable empty state with
    // current settings — keeps the empty-state DOM as a single source of truth.
    window.location.reload();
  }

  /* ------------------------------- helpers ----------------------------- */

  formatMoney(cents) {
    const amount = (cents || 0) / 100;
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }
}

if (!customElements.get('heritage-cart')) {
  customElements.define('heritage-cart', HeritageCart);
}
